/**
 * Promo free-gift abuse guard — <promo-gift-guard>
 *
 * Removes gift lines from the cart when the last main product for the same
 * _landing_source is gone. Example: _landing_source = "nektar-glp1".
 *
 * Also watches cart mutations involving `__cart_gift_tier` (custom free-gift
 * app): successful POST /cart/add.js that adds a tier gift, and
 * /cart/update.js|/cart/change.js when the gift-tier line count changes
 * (gift removed under threshold, etc.). Silently refreshes cart-drawer +
 * icon bubble. Same refresh runs after this guard removes orphan gifts.
 *
 * Landing sections pass customizer settings as attributes (nothing hardcoded):
 *
 *   <promo-gift-guard
 *     landing-source="nektar-glp1"
 *     gift-variant-ids="32770500657237"
 *   ></promo-gift-guard>
 *
 * Cart line properties:
 *   _landing_source  — page / promo id on the main product and its gifts
 *   _promo_gift      — "true" on gift lines only
 *   __landing_source — same value, for existing discount-function configs
 *   __cart_gift_tier — tier id from the custom gift app (e.g. "tier-1")
 *
 * A layout-level <promo-gift-guard> with no attributes still runs on cart /
 * drawer pages, using those line properties.
 */
(function () {
  var LANDING_SOURCE_KEYS = ["_landing_source", "__landing_source"];
  var PROMO_GIFT_KEY = "_promo_gift";
  var CART_GIFT_TIER_KEY = "__cart_gift_tier";
  var CART_MUTATION_URL_PATTERN = /\/cart\/(change|update|add|clear)\.js/;
  var CART_ADD_URL_PATTERN = /\/cart\/add\.js/;
  var CART_UPDATE_URL_PATTERN = /\/cart\/(change|update)\.js/;

  var checking = false;
  var recheckAfter = false;
  var watcherInstalled = false;
  var drawerRefreshTimer = null;
  var lastGiftTierCount = 0;
  var giftUiDirty = false;
  var originalFetch = null;

  function parseVariantIds(raw) {
    if (!raw) return [];
    var text = String(raw).trim();
    if (!text) return [];
    var values = [];
    if (text.charAt(0) === "[") {
      try {
        values = JSON.parse(text);
      } catch (e) {
        values = [];
      }
    }
    if (!Array.isArray(values) || values.length === 0) {
      values = text.split(/[\s,]+/);
    }
    return values
      .map(function (value) {
        return Number(value);
      })
      .filter(function (id) {
        return !!id;
      });
  }

  function prop(item, key) {
    if (!item || !item.properties) return "";
    var value = item.properties[key];
    if (value == null) return "";
    return String(value).trim();
  }

  function isTruthyFlag(value) {
    return value === "true" || value === "1" || value === true;
  }

  function landingSource(item) {
    for (var i = 0; i < LANDING_SOURCE_KEYS.length; i++) {
      var value = prop(item, LANDING_SOURCE_KEYS[i]);
      if (value) return value;
    }
    return "";
  }

  function collectElementConfigs() {
    var configs = [];
    document.querySelectorAll("promo-gift-guard").forEach(function (el) {
      configs.push({
        landingSource: el.landingSource,
        giftVariantIds: el.giftVariantIds,
      });
    });
    return configs;
  }

  function isGiftLine(item, configs) {
    var giftProp = prop(item, PROMO_GIFT_KEY);
    if (giftProp) return true;

    var source = landingSource(item);
    if (!source) return false;
    var variantId = Number(item.variant_id);
    for (var i = 0; i < configs.length; i++) {
      var config = configs[i];
      if (config.landingSource !== source) continue;
      if (config.giftVariantIds.indexOf(variantId) !== -1) return true;
    }
    return false;
  }

  function orphanGiftKeys(cart) {
    var items = (cart && cart.items) || [];
    var configs = collectElementConfigs();
    var hasMain = {};
    var giftKeysBySource = {};

    items.forEach(function (item) {
      var source = landingSource(item);
      if (!source) return;

      if (isGiftLine(item, configs)) {
        if (!giftKeysBySource[source]) giftKeysBySource[source] = [];
        giftKeysBySource[source].push(item.key);
        return;
      }
      hasMain[source] = true;
    });

    var keys = [];
    Object.keys(giftKeysBySource).forEach(function (source) {
      if (hasMain[source]) return;
      giftKeysBySource[source].forEach(function (key) {
        keys.push(key);
      });
    });
    return keys;
  }

  function cartFetch(url, options) {
    var fetchFn = originalFetch || window.fetch;
    return fetchFn.call(window, url, options);
  }

  function removeLines(keys) {
    var updates = {};
    keys.forEach(function (key) {
      updates[key] = 0;
    });
    // Bypass the fetch wrapper so our own orphan cleanup does not re-enter checkCart.
    return cartFetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ updates: updates }),
    });
  }

  function notifyCartUi(cart) {
    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "promo-gift-guard", cartData: cart });
    }
  }

  function extractAddedItems(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.items)) return payload.items;
    if (payload.properties || payload.variant_id || payload.key) return [payload];
    return [];
  }

  function countGiftTiers(items) {
    if (!items || !items.length) return 0;
    var count = 0;
    for (var i = 0; i < items.length; i++) {
      if (prop(items[i], CART_GIFT_TIER_KEY)) count += 1;
    }
    return count;
  }

  function hasCartGiftTierItems(payload) {
    return countGiftTiers(extractAddedItems(payload)) > 0;
  }

  /**
   * Custom gift app mutates the cart without section HTML. Re-fetch
   * cart-drawer + icon bubble in place; keep the drawer open (no reopen flash).
   */
  function refreshCartDrawerSilently() {
    if (drawerRefreshTimer) clearTimeout(drawerRefreshTimer);
    drawerRefreshTimer = setTimeout(function () {
      drawerRefreshTimer = null;
      var drawer = document.querySelector("cart-drawer");
      if (drawer && typeof drawer.refreshSections === "function") {
        drawer.refreshSections({ openDrawer: false });
        return;
      }
      notifyCartUi(null);
    }, 80);
  }

  function maybeRefreshDrawerForGiftMutation(response, url) {
    if (!response || !response.ok || typeof response.clone !== "function") return;
    var isAdd = CART_ADD_URL_PATTERN.test(url);
    var isUpdate = CART_UPDATE_URL_PATTERN.test(url);
    if (!isAdd && !isUpdate) return;

    response
      .clone()
      .json()
      .then(function (payload) {
        if (isAdd) {
          if (hasCartGiftTierItems(payload)) {
            giftUiDirty = true;
            refreshCartDrawerSilently();
          }
          return;
        }

        // update.js / change.js return the full cart — refresh when gift-tier lines
        // appear/disappear, or after a gift add before /cart.js has synced the count.
        var nextCount = countGiftTiers(payload && payload.items);
        if (giftUiDirty || nextCount !== lastGiftTierCount) {
          lastGiftTierCount = nextCount;
          giftUiDirty = false;
          refreshCartDrawerSilently();
        }
      })
      .catch(function () {});
  }

  function checkCart() {
    if (checking) {
      recheckAfter = true;
      return;
    }
    checking = true;

    cartFetch("/cart.js", { credentials: "same-origin" })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        lastGiftTierCount = countGiftTiers(cart && cart.items);
        giftUiDirty = false;
        var giftKeys = orphanGiftKeys(cart);
        if (giftKeys.length === 0) return;
        return removeLines(giftKeys).then(function (response) {
          if (!response || !response.ok) return;
          return response.json().then(function (updatedCart) {
            lastGiftTierCount = countGiftTiers(updatedCart && updatedCart.items);
            giftUiDirty = false;
            notifyCartUi(updatedCart);
            refreshCartDrawerSilently();
          });
        });
      })
      .catch(function () {
        /* best-effort UX only — the discount function already stops
           giving the gift away once the main product is gone */
      })
      .finally(function () {
        checking = false;
        if (recheckAfter) {
          recheckAfter = false;
          checkCart();
        }
      });
  }

  function installCartWatcher() {
    if (watcherInstalled) return;
    watcherInstalled = true;

    originalFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var isCartMutation = CART_MUTATION_URL_PATTERN.test(url);
      var result = originalFetch.apply(window, arguments);
      if (isCartMutation) {
        result
          .then(function (response) {
            if (response && response.ok) {
              checkCart();
              maybeRefreshDrawerForGiftMutation(response, url);
            }
            return response;
          })
          .catch(function () {});
      }
      return result;
    };
  }

  class PromoGiftGuard extends HTMLElement {
    static get observedAttributes() {
      return ["landing-source", "gift-variant-ids"];
    }

    get landingSource() {
      return (this.getAttribute("landing-source") || "").trim();
    }

    get giftVariantIds() {
      return parseVariantIds(this.getAttribute("gift-variant-ids"));
    }

    connectedCallback() {
      installCartWatcher();
      checkCart();
    }
  }

  if (!customElements.get("promo-gift-guard")) {
    customElements.define("promo-gift-guard", PromoGiftGuard);
  }
})();
